import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useTranslation } from 'react-i18next';
import { personalInfo } from '../../data/portfolio';

/* ─── Brand SVG icons ─────────────────────────────────────────── */
const EmailIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
  </svg>
);

const LinkedInIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
    <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
    <rect x="2" y="9" width="4" height="12" />
    <circle cx="4" cy="4" r="2" />
  </svg>
);

const GitHubIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22" />
  </svg>
);

const GitLabIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
    <path d="m22 13.29-3.33-10a.42.42 0 0 0-.14-.18.38.38 0 0 0-.22-.11.39.39 0 0 0-.23.07.42.42 0 0 0-.14.18l-2.26 6.67H8.32L6.06 3.26a.42.42 0 0 0-.14-.18.38.38 0 0 0-.22-.11.39.39 0 0 0-.23.07.42.42 0 0 0-.14.18L2 13.29a.74.74 0 0 0 .27.83L12 21l9.69-6.88a.71.71 0 0 0 .31-.83z" />
  </svg>
);

const ChevronIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

/* ─── Styled components ───────────────────────────────────────── */
const Section = styled.section`
  padding: 100px 20px;
  max-width: 860px;
  margin: 0 auto;
  text-align: center;
`;

const SectionLabel = styled(motion.p)`
  font-size: 13px;
  color: var(--clr-text-faint);
  letter-spacing: 2.5px;
  text-transform: uppercase;
  margin-bottom: 12px;
  font-weight: 500;
`;

const Title = styled(motion.h2)`
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 16px;
  font-size: clamp(30px, 5vw, 44px);
  font-weight: 700;
  letter-spacing: -0.5px;
`;

const TitleAccent = styled.span`
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.primaryDark});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Description = styled(motion.p)`
  color: var(--clr-text-dim);
  font-size: 16px;
  max-width: 480px;
  margin: 0 auto 52px;
  line-height: 1.7;
`;

const ContactGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 14px;
  margin-bottom: 16px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const ContactLink = styled(motion.a)<{ $accent: string }>`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 18px 20px;
  border-radius: 14px;
  background: ${({ theme }) => theme.colors.backgroundCard};
  border: 1.5px solid var(--clr-surface-border);
  box-shadow: ${({ theme }) => theme.shadows.card};
  text-decoration: none;
  transition: border-color 0.25s ease, box-shadow 0.25s ease, transform 0.2s ease;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: ${({ $accent }) => $accent};
    opacity: 0;
    transition: opacity 0.25s ease;
    border-radius: inherit;
  }

  &:hover {
    border-color: ${({ $accent }) => $accent}66;
    box-shadow: 0 8px 30px ${({ $accent }) => $accent}22, ${({ theme }) => theme.shadows.card};
    transform: translateY(-2px);

    &::before {
      opacity: 0.04;
    }
  }
`;

const IconBox = styled.div<{ $bg: string }>`
  width: 46px;
  height: 46px;
  border-radius: 12px;
  background: ${({ $bg }) => $bg};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  position: relative;
  z-index: 1;
`;

const LinkContent = styled.div`
  text-align: left;
  flex: 1;
  min-width: 0;
  position: relative;
  z-index: 1;
`;

const LinkLabel = styled.span`
  display: block;
  font-size: 11px;
  color: var(--clr-text-faint);
  text-transform: uppercase;
  letter-spacing: 1px;
  font-weight: 600;
  margin-bottom: 3px;
`;

const LinkValue = styled.span`
  display: block;
  font-size: 14.5px;
  color: var(--clr-text-bright);
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Chevron = styled.div`
  color: var(--clr-text-hint);
  flex-shrink: 0;
  display: flex;
  align-items: center;
  position: relative;
  z-index: 1;
  transition: color 0.2s ease, transform 0.2s ease;

  ${ContactLink}:hover & {
    transform: translateX(2px);
    color: var(--clr-text-faint);
  }
`;

const LocationBar = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 14px 24px;
  border-radius: 12px;
  background: ${({ theme }) => theme.colors.backgroundCard};
  border: 1.5px solid var(--clr-surface-border);
  box-shadow: ${({ theme }) => theme.shadows.card};
  margin-top: 14px;
`;

const PulseDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #22c55e;
  box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.18);
  flex-shrink: 0;
`;

const LocationText = styled.span`
  font-size: 13.5px;
  color: var(--clr-text-dim);
  font-weight: 450;
`;

/* ─── Data ─────────────────────────────────────────────────────── */
const contactItems = [
  {
    label: 'Email',
    value: personalInfo.contact.email,
    href: `mailto:${personalInfo.contact.email}`,
    Icon: EmailIcon,
    bg: '#e5484d',
    accent: '#ef4444',
    external: false,
  },
  {
    label: 'LinkedIn',
    value: 'achref-samoud',
    href: personalInfo.contact.linkedin || '#',
    Icon: LinkedInIcon,
    bg: '#0a66c2',
    accent: '#0a66c2',
    external: true,
  },
  {
    label: 'GitHub',
    value: 'AchrefSamoud',
    href: personalInfo.contact.github || '#',
    Icon: GitHubIcon,
    bg: '#24292e',
    accent: '#6e7681',
    external: true,
  },
  {
    label: 'GitLab',
    value: 'achrefsam',
    href: personalInfo.contact.gitlab || '#',
    Icon: GitLabIcon,
    bg: '#e24329',
    accent: '#fc6d26',
    external: true,
  },
];

const itemVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.45, ease: 'easeOut' as const },
  }),
};

/* ─── Component ─────────────────────────────────────────────────── */
const Contact: React.FC = () => {
  const { t } = useTranslation();
  const { ref, inView } = useInView({ threshold: 0.1, triggerOnce: true });

  return (
    <Section id="contact" ref={ref}>
      <SectionLabel
        initial={{ opacity: 0, y: 16 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.5 }}
      >
        {t('contact.sectionLabel')}
      </SectionLabel>

      <Title
        initial={{ opacity: 0, y: 16 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.5, delay: 0.08 }}
      >
        {t('contact.title')} <TitleAccent>{t('contact.titleAccent')}</TitleAccent>
      </Title>

      <Description
        initial={{ opacity: 0, y: 16 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.5, delay: 0.16 }}
      >
        {t('contact.description')}
      </Description>

      <ContactGrid initial="hidden" animate={inView ? 'visible' : 'hidden'}>
        {contactItems.map((item, i) => (
          <ContactLink
            key={item.label}
            href={item.href}
            target={item.external ? '_blank' : undefined}
            rel={item.external ? 'noopener noreferrer' : undefined}
            $accent={item.accent}
            custom={i}
            variants={itemVariants}
          >
            <IconBox $bg={item.bg}>
              <item.Icon />
            </IconBox>
            <LinkContent>
              <LinkLabel>{item.label}</LinkLabel>
              <LinkValue>{item.value}</LinkValue>
            </LinkContent>
            <Chevron>
              <ChevronIcon />
            </Chevron>
          </ContactLink>
        ))}
      </ContactGrid>

      <LocationBar
        initial={{ opacity: 0, y: 16 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.5, delay: 0.5 }}
      >
        <PulseDot />
        <LocationText>{t('contact.location')}</LocationText>
      </LocationBar>
    </Section>
  );
};

export default Contact;